import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/vehicleSparesDetailsReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

export default function VehicleSpareDetailReport_Print() {
  const navigate = useNavigate();
  const { state, search } = useLocation();

  // ✅ supports both querystring and navigate(state)
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const filters = useMemo(() => {
    const f = state?.filters || {};
    return {
      vehiclecode: toStr(f.vehiclecode || qs.get("vehiclecode")),
      branchid: toStr(f.branchid || qs.get("branchid") || "Select"),
      fromdate: toStr(f.fromdate || qs.get("fromdate")),
      todate: toStr(f.todate || qs.get("todate")),
      itemname: toStr(f.itemname || qs.get("itemname") || "%"),
      // optional extra filters if you have them in SP
      // category: toStr(f.category || qs.get("category") || "Select"),
      // supplier: toStr(f.supplier || qs.get("supplier") || "Select"),
    };
  }, [state, qs]);

  const [loading, setLoading] = useState(false);
  const [mainRows, setMainRows] = useState([]);      // main report (Crystal main table)
  const [issueRows, setIssueRows] = useState([]);    // subreport[0] rows
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");

  const handlePrint = () => window.print();

  const fetchPrint = async () => {
    try {
      setError("");
      setLoading(true);

      const res = await axios.get(`${API_BASE}/list`, { params: filters });
      if (!res.data?.success) throw new Error(res.data?.message || "Failed to load");

      setMainRows(Array.isArray(res.data.mainRows) ? res.data.mainRows : []);
      setIssueRows(Array.isArray(res.data.issueRows) ? res.data.issueRows : []);
      setMeta(res.data.meta || null);
    } catch (e) {
      setError(e.message || "Failed to load print data");
      setMainRows([]);
      setIssueRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.vehiclecode, filters.branchid, filters.fromdate, filters.todate, filters.itemname]);

  // safe field reader for mixed column names
  const get = (obj, ...keys) => {
    for (const k of keys) {
      if (obj && obj[k] !== undefined && obj[k] !== null && String(obj[k]).trim() !== "") return obj[k];
    }
    return "";
  };

  // meta header values: prefer from meta, else from first row
  const headerRow = mainRows?.[0] || issueRows?.[0] || {};
  const hdr = {
    vehicle: get(meta, "vehicle", "vehiclename", "fld_vehiclename", "vehicleName") || get(headerRow, "vehiclename", "fld_vehiclename", "vehicleName"),
    regno: get(meta, "regno", "vehicleregno", "fld_vehicleregno", "vehicleRegNo") || get(headerRow, "vehicleregno", "fld_vehicleregno", "vehicleRegNo"),
    vehiclecode: filters.vehiclecode || get(meta, "vehiclecode") || get(headerRow, "vehiclecode", "fld_vehiclecode"),
    branch: get(meta, "branch", "branchname", "fld_branchname") || get(headerRow, "branchname", "fld_branchname") || filters.branchid,
    fromdate: filters.fromdate,
    todate: filters.todate,
    itemname: filters.itemname,
  };

  return (
    <div className="min-h-screen bg-white p-4">
      {/* Buttons hidden during print */}
      <div className="print:hidden mb-6 text-center flex gap-3 justify-center">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 text-black px-6 py-3 text-sm font-medium hover:bg-gray-300"
        >
          Back
        </button>

        <button
          onClick={handlePrint}
          disabled={loading}
          className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-60"
        >
          {loading ? "Loading..." : "Print Report"}
        </button>
      </div>

      <div className="max-w-6xl mx-auto border-2 border-black p-4 print:max-w-none print:mx-0 print:border-2">
        <div className="border border-black p-3">
          {/* Header (same as your template) */}
          <div className="border border-black p-3 mb-4 flex items-center gap-5">
            <div className="w-28 h-20 bg-black text-white flex flex-col justify-center items-center text-xs font-bold">
              <div>NISSI</div>
              <div>INFOTECH</div>
              <div className="text-xs">PVT LTD</div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold mb-2">Nissi Infotech</h1>
              <div className="text-xs leading-tight">
                <div>Nissi</div>
                <div className="font-semibold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony , kavundampalayam ,Tamil Nadu, India .</div>
                <div className="mt-1">
                  Phone No :9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infraconsoft-implementation@nissiinfotech.in
                </div>
              </div>
            </div>
          </div>

          <div className="text-center text-base font-bold my-4">Vehicle Spare Detail Report</div>

          {/* Filter/Meta line */}
          <div className="border border-black p-2 text-xs mb-4">
            <div className="grid grid-cols-2 gap-2">
              <div><span className="font-semibold">Branch</span> : {hdr.branch}</div>
              <div><span className="font-semibold">Vehicle</span> : {hdr.vehicle}</div>
              <div><span className="font-semibold">Reg No</span> : {hdr.regno}</div>
              <div><span className="font-semibold">Vehicle Code</span> : {hdr.vehiclecode}</div>
              <div><span className="font-semibold">From</span> : {hdr.fromdate || "-"}</div>
              <div><span className="font-semibold">To</span> : {hdr.todate || "-"}</div>
              <div className="col-span-2"><span className="font-semibold">Item</span> : {hdr.itemname || "%"}</div>
            </div>
          </div>

          {/* Errors / No data */}
          {error ? (
            <div className="text-center text-sm p-6 border border-black">{error}</div>
          ) : !loading && mainRows.length === 0 && issueRows.length === 0 ? (
            <div className="text-center text-sm p-6 border border-black">No data found.</div>
          ) : (
            <>
              {/* MAIN TABLE (like Crystal main report) */}
              <div className="mb-6">
                <div className="font-bold text-sm mb-2">Spare Summary</div>
                <div className="border border-black overflow-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-black">
                        {buildColumns(mainRows, [
                          "fld_itemname",
                          "itemname",
                          "fld_spare",
                          "spare",
                          "fld_qty",
                          "qty",
                          "fld_rate",
                          "rate",
                          "fld_amount",
                          "amount",
                        ]).map((c) => (
                          <th key={c.key} className="border-r border-black p-2 text-left font-semibold whitespace-nowrap">
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mainRows.map((r, idx) => (
                        <tr key={idx} className="border-b border-black">
                          {buildColumns(mainRows).map((c) => (
                            <td key={c.key} className="border-r border-black p-2 whitespace-nowrap">
                              {toStr(r[c.key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* SUBREPORT TABLE (Crystal Subreports[0]) */}
              <div className="mb-2">
                <div className="font-bold text-sm mb-2">Issue Details</div>
                <div className="border border-black overflow-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-black">
                        {buildColumns(issueRows, [
                          "fld_issuedate",
                          "issuedate",
                          "fld_issue_no",
                          "issueNo",
                          "fld_itemname",
                          "itemname",
                          "fld_qty",
                          "qty",
                          "fld_remarks",
                          "remarks",
                        ]).map((c) => (
                          <th key={c.key} className="border-r border-black p-2 text-left font-semibold whitespace-nowrap">
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {issueRows.map((r, idx) => (
                        <tr key={idx} className="border-b border-black">
                          {buildColumns(issueRows).map((c) => (
                            <td key={c.key} className="border-r border-black p-2 whitespace-nowrap">
                              {toStr(r[c.key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <div className="mt-6 p-2 border border-black bg-gray-100 text-xs">
            Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
          </div>
        </div>
      </div>

      {/* print css */}
      <style jsx>{`
        @media print {
          body { margin: 0; padding: 0; }
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden { display: none !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:mx-0 { margin-left: 0 !important; margin-right: 0 !important; }
          .print\\:border-2 { border-width: 2px !important; }
          table { page-break-inside: auto; }
          tr, td, th { page-break-inside: avoid; page-break-after: auto; }
        }
      `}</style>
    </div>
  );
}

/**
 * Builds columns dynamically from rows:
 * - if you pass preferredKeys, it tries to use those first (if they exist)
 * - else it uses keys from first row
 */
function buildColumns(rows, preferredKeys = []) {
  if (!Array.isArray(rows) || rows.length === 0) {
    // fallback for empty table (still show something stable)
    const fallback = preferredKeys.filter((_, i) => i % 2 === 0).slice(0, 6);
    return fallback.map((k) => ({ key: k, label: prettify(k) }));
  }

  const sample = rows[0] || {};
  const keys = Object.keys(sample);

  let chosen = [];
  if (preferredKeys.length) {
    // preferredKeys is [k1,k2,k3...] - we only include keys that exist
    chosen = preferredKeys.filter((k) => keys.includes(k));
  }
  if (!chosen.length) chosen = keys;

  // limit huge wide tables
  const limited = chosen.slice(0, 12);

  return limited.map((k) => ({ key: k, label: prettify(k) }));
}

function prettify(k) {
  return String(k)
    .replace(/^fld_/i, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}